/**
 * What a route shows while it is still resolving.
 *
 * Two shapes, because the two waits are not the same one. `/_app` resolves the session before the
 * shell exists, so its pending component has to draw the chrome itself — a pendingComponent renders
 * *instead of* the route's component, and `/_app`'s component IS the AppShell. Everything below it
 * renders inside a shell that is already on screen, so it only needs page-shaped content.
 */
import { ListPageSkeleton } from "@/components/app/skeletons";
import { Progress } from "@/components/ui/progress";
import { SeedlingMark } from "@/components/app/seedling-mark";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The router-wide default. In practice only `/` and `/login` reach it — they are the only other
 * routes that await `bootstrap()`, and no route in the app has a loader — so it is deliberately the
 * brand mark rather than a page skeleton: a list skeleton behind a sign-in form would be a lie
 * about what is coming.
 */
export function RoutePending() {
  return (
    <div className="grid h-full place-content-center justify-items-center gap-4">
      <SeedlingMark className="h-10 w-10" />
      <Progress className="w-40" />
      <span className="sr-only" role="status">
        Loading NexSeed
      </span>
    </div>
  );
}

/**
 * The cold-start screen: real top-nav geometry with placeholder contents, so the app looks like itself
 * within one frame rather than flashing white while `bootstrap()` round-trips.
 */
export function AppShellPending() {
  return (
    <div className="flex h-full flex-col">
      {/* Matches TopNav: a 56px soil strip over a 48px menu bar. */}
      <div className="flex h-14 shrink-0 items-center gap-3 bg-forest-900 px-5">
        <Skeleton className="h-9 w-9 rounded-lg bg-white/15" />
        <Skeleton className="h-4 w-32 bg-white/15" />
        <Skeleton className="ml-auto h-8 w-8 rounded-full bg-white/15" />
      </div>
      <div className="flex h-12 shrink-0 items-center gap-6 border-b border-border bg-surface-raised px-5">
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="h-3.5 w-20" />
        ))}
      </div>
      <main className="min-w-0 flex-1 overflow-hidden p-4 md:p-6">
        <ListPageSkeleton />
      </main>
      <span className="sr-only" role="status">
        Loading NexSeed
      </span>
    </div>
  );
}
